import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Spin } from 'antd'
import styled from 'styled-components'
import B2BLayout from '../../layouts/b2b'
import fetchAPI from '../../../lib/utils/fetch-api'
import { IframeElement, IframeWrapper, IframeLoader } from '../ai-reports/styles'
import { configuredApiOrigin, fetchDashboardAPI } from './api'

const DashboardFrame = styled(IframeWrapper)`
  box-sizing: border-box;
  padding: 24px;

  .dashboard-refresh { position: absolute; right: 40px; bottom: 40px; z-index: 2; }

  @media (max-width: 768px) {
    padding: 12px;
    .dashboard-refresh { right: 28px; bottom: 28px; }
  }
`

type EmbedToken = { accessToken: string; expiresAt: string }

export function SvetoforDashboardPage() {
  const iframe = useRef<HTMLIFrameElement>(null)
  const pending = useRef<Promise<EmbedToken> | null>(null)
  const [src, setSrc] = useState('')
  const [apiOrigin, setApiOrigin] = useState(configuredApiOrigin || window.location.origin)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let active = true
    fetchDashboardAPI('/api/svetofor-dashboard/config').then(config => { if (active) { setApiOrigin(config.publicApiOrigin || configuredApiOrigin || window.location.origin); setSrc(config.url) } })
      .catch(cause => { if (active) { setError(cause.message); setLoading(false) } })
    return () => { active = false }
  }, [])

  const postHostConfig = useCallback(async (target: Window, origin: string) => {
    try {
      if (!pending.current) pending.current = fetchAPI(`${apiOrigin}/api/mcp2/embed-token`, { timeoutMs: 20000 }).then(r => r.data).finally(() => { pending.current = null })
      const token = await pending.current
      if (!token?.accessToken) throw new Error('Не удалось подтвердить вход в ERP')
      if (target !== iframe.current?.contentWindow) return
      target.postMessage({ type: 'mcp-ui:host-config', payload: {
        apiBase: `${apiOrigin}/api/svetofor-dashboard`, navigationBase: window.location.origin,
        accessControlEnabled: true, accessToken: token.accessToken, accessTokenExpiresAt: token.expiresAt,
      } }, origin)
      setError('')
    } catch (cause) {
      if (target === iframe.current?.contentWindow) { setError((cause as Error).message); setLoading(false) }
    }
  }, [apiOrigin])

  useEffect(() => {
    if (!src) return
    const origin = new URL(src).origin
    const onMessage = (event: MessageEvent) => {
      const target = iframe.current?.contentWindow
      if (!target || event.origin !== origin || event.source !== target || event.data?.type !== 'mcp-ui:request-host-config') return
      void postHostConfig(target, origin)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [src, postHostConfig])

  return <B2BLayout pageTitle="Сводная" breadcrumbs={[{ href: '/dashboard', title: 'Сводная' }]}>
    {error && <Alert type="error" showIcon message={error} />}
    <DashboardFrame $fullBleed>
      {loading && <IframeLoader><Spin size="large" aria-label="Загрузка сводной Светофора" /></IframeLoader>}
      {src && <IframeElement ref={iframe} key={version} title="Сводная Светофора" src={src}
        referrerPolicy="strict-origin-when-cross-origin" allow="fullscreen" onLoad={() => {
          setLoading(false)
          const target = iframe.current?.contentWindow
          if (target) void postHostConfig(target, new URL(src).origin)
        }} />}
      <div className="dashboard-refresh">
        <Button onClick={() => { setError(''); setLoading(true); setVersion(value => value + 1) }}>Обновить сводную</Button>
      </div>
    </DashboardFrame>
  </B2BLayout>
}
